import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Archive, CalendarClock, CheckCircle2, ChevronRight, Clock3, Copy, FileText, FolderOpen, LayoutTemplate, ListTodo, MoreHorizontal, Palette, Plus, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { GPRIORITY, fmtD, gstatus } from "@/components/graphisme/gUtils";
import ProjectPanel from "@/components/graphisme/ProjectPanel";
import { SERVICE_TYPES, isGraphismeClient } from "@/lib/constants";

const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";

function QuickClientModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("graphisme");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setType("graphisme"); } }, [open]);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/clients", { name: name.trim(), service_type: type });
      toast.success(`Client « ${data.name} » créé — fiche complétée plus tard`);
      onCreated(data);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="gp-quick-client" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Nouveau client (création rapide)</DialogTitle>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Entreprise ou Nom/Prénom *</Label>
            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Maison Luma" data-testid="gp-quick-client-name"
              onKeyDown={(e) => e.key === "Enter" && submit()} autoFocus />
          </div>
          <div>
            <Label className="text-xs">Type de prestation</Label>
            <select className={`${sel} mt-1`} value={type} onChange={(e) => setType(e.target.value)} data-testid="gp-quick-client-type">
              {SERVICE_TYPES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-neutral-400">La fiche centrale est créée immédiatement — les autres informations pourront être complétées ensuite.</p>
          <Button onClick={submit} disabled={busy || !name.trim()} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gp-quick-client-save">
            {busy ? "Création…" : "Créer et sélectionner ce client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProjectForm({ open, onClose, onSaved, clients, meta, project = null, onClientCreated }) {
  const isEdit = !!project;
  const empty = { client_id: "", name: "", description: "", brief: "", objectif: "", types: [],
    qty_creations: 1, formats: "", dimensions: "", supports: "", delai: "", deadline: "",
    priority: "normal", estimated_minutes: "", corrections_included: 2, price_ht: "",
    responsible: "", elementsText: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [quickOpen, setQuickOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");

  useEffect(() => {
    if (!open) return;
    if (project) {
      setF({ ...empty, ...project, price_ht: project.price_ht || "",
        estimated_minutes: project.estimated_minutes || "",
        elementsText: (project.elements || []).map((e) => e.label).join("\n") });
    } else setF(empty);
    setTplId(""); setClientSearch("");
    if (!project) api.get("/graphisme/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [open, project]);

  const toggleType = (t) => setF((x) => ({ ...x, types: x.types.includes(t) ? x.types.filter((y) => y !== t) : [...x.types, t] }));

  const applyTemplate = (id) => {
    setTplId(id);
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setF((x) => ({
      ...x,
      types: x.types.length ? x.types : (tpl.types || []),
      estimated_minutes: x.estimated_minutes || tpl.estimated_minutes || "",
      price_ht: x.price_ht || tpl.price_ht || "",
      corrections_included: tpl.corrections_included ?? x.corrections_included,
      formats: x.formats || tpl.formats || "",
    }));
  };

  const filteredClients = clients.filter((c) =>
    (isGraphismeClient(c) || c.id === f.client_id) &&
    c.name.toLowerCase().includes(clientSearch.toLowerCase()));

  const submit = async () => {
    if (!f.client_id) { toast.error("Sélectionnez un client"); return; }
    if (!f.name.trim()) { toast.error("Nom du projet requis"); return; }
    setBusy(true);
    const payload = { ...f, qty_creations: +f.qty_creations || 1,
      estimated_minutes: +f.estimated_minutes || 0, corrections_included: +f.corrections_included || 0,
      price_ht: +String(f.price_ht).replace(",", ".") || 0,
      elements: f.elementsText.split("\n").map((l) => ({ label: l.trim() })).filter((e) => e.label) };
    delete payload.elementsText;
    if (tplId) payload.template_id = tplId;
    try {
      if (isEdit) {
        await api.patch(`/graphisme/projects/${project.id}`, payload);
        toast.success("Projet mis à jour");
      } else {
        await api.post("/graphisme/projects", payload);
        toast.success("Projet créé");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gproject-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">
          {isEdit ? `Modifier le projet ${project?.number || ""}` : "Nouveau projet graphique"}
        </DialogTitle>
        <div className="space-y-4 mt-2">
          {!isEdit && templates.length > 0 && (
            <div>
              <Label className="text-xs">Modèle de projet (préremplit le formulaire)</Label>
              <select className={`${sel} mt-1`} value={tplId} onChange={(e) => applyTemplate(e.target.value)} data-testid="gp-template">
                <option value="">Aucun modèle — partir de zéro</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({(t.creations || []).length} création(s))</option>)}
              </select>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Client *</Label>
              <Input className="mt-1 mb-1.5" placeholder="Rechercher un client…" value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)} data-testid="gp-client-search" />
              <select className={sel} value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="gp-client">
                <option value="">Sélectionner…</option>
                {filteredClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => setQuickOpen(true)} data-testid="gp-add-client"
                className="mt-1.5 text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors">
                <Plus className="w-3 h-3" /> Ajouter un nouveau client
              </button>
            </div>
            <div>
              <Label className="text-xs">Nom du projet *</Label>
              <Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex : Nouvelle carte des soins" data-testid="gp-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} className="mt-1" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="gp-description" />
          </div>
          <div>
            <Label className="text-xs">Brief</Label>
            <Textarea rows={3} className="mt-1" value={f.brief} onChange={(e) => setF({ ...f, brief: e.target.value })} placeholder="Contexte, attentes, références…" data-testid="gp-brief" />
          </div>
          <div>
            <Label className="text-xs">Type(s) de création</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="gp-types">
              {(meta?.creation_types || []).map((t) => (
                <button key={t} type="button" onClick={() => toggleType(t)} data-testid={`gp-type-${t}`}
                  className={`text-xs border rounded-full px-3 py-1.5 transition-colors ${f.types.includes(t) ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-200 hover:border-neutral-400"}`}>
                  {t}
                </button>
              ))}
            </div>
            <Input className="mt-2" placeholder="Type personnalisé… (Entrée pour ajouter)" data-testid="gp-custom-type"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { e.preventDefault(); toggleType(e.target.value.trim()); e.target.value = ""; } }} />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Nb de créations</Label>
              <Input type="number" min="1" className="mt-1" value={f.qty_creations} onChange={(e) => setF({ ...f, qty_creations: e.target.value })} data-testid="gp-qty" /></div>
            <div><Label className="text-xs">Formats</Label>
              <Input className="mt-1" value={f.formats} onChange={(e) => setF({ ...f, formats: e.target.value })} placeholder="A5, 1080×1080…" /></div>
            <div><Label className="text-xs">Dimensions</Label>
              <Input className="mt-1" value={f.dimensions} onChange={(e) => setF({ ...f, dimensions: e.target.value })} placeholder="210×297 mm…" /></div>
            <div><Label className="text-xs">Supports</Label>
              <Input className="mt-1" value={f.supports} onChange={(e) => setF({ ...f, supports: e.target.value })} placeholder="Print, web…" /></div>
            <div><Label className="text-xs">Délai souhaité</Label>
              <Input className="mt-1" value={f.delai} onChange={(e) => setF({ ...f, delai: e.target.value })} placeholder="Ex : 2 semaines" /></div>
            <div><Label className="text-xs">Deadline</Label>
              <Input type="date" className="mt-1" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="gp-deadline" /></div>
            <div><Label className="text-xs">Priorité</Label>
              <select className={`${sel} mt-1`} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="gp-priority">
                <option value="normal">Normal</option><option value="prioritaire">Prioritaire</option><option value="urgent">Urgent</option>
              </select></div>
            <div><Label className="text-xs">Temps estimé (min)</Label>
              <Input type="number" min="0" className="mt-1" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} data-testid="gp-estimated" /></div>
            <div><Label className="text-xs">Allers-retours inclus</Label>
              <Input type="number" min="0" className="mt-1" value={f.corrections_included} onChange={(e) => setF({ ...f, corrections_included: e.target.value })} data-testid="gp-corrections" /></div>
            <div><Label className="text-xs">Prix HT (€)</Label>
              <Input type="number" min="0" className="mt-1" value={f.price_ht} onChange={(e) => setF({ ...f, price_ht: e.target.value })} data-testid="gp-price" /></div>
            <div><Label className="text-xs">Responsable</Label>
              <Input className="mt-1" value={f.responsible} onChange={(e) => setF({ ...f, responsible: e.target.value })} placeholder="Aurélie" /></div>
            <div><Label className="text-xs">Objectif</Label>
              <Input className="mt-1" value={f.objectif} onChange={(e) => setF({ ...f, objectif: e.target.value })} placeholder="But de la création" /></div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Éléments nécessaires au projet (un par ligne — ex : Logo HD, Textes, Photos, Charte graphique)</Label>
              <Textarea rows={3} className="mt-1" value={f.elementsText} onChange={(e) => setF({ ...f, elementsText: e.target.value })} data-testid="gp-elements" />
            </div>
          )}
          <Button onClick={submit} disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gp-save">
            {busy ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer le projet"}
          </Button>
        </div>
        <QuickClientModal open={quickOpen} onClose={() => setQuickOpen(false)}
          onCreated={(c) => { setF((x) => ({ ...x, client_id: c.id })); onClientCreated?.(c); }} />
      </DialogContent>
    </Dialog>
  );
}

function TemplatesModal({ open, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", price_ht: "", estimated_minutes: "", corrections_included: 2, formats: "", creationsText: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/graphisme/templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Nom du modèle requis"); return; }
    setBusy(true);
    try {
      await api.post("/graphisme/templates", {
        name: f.name.trim(), price_ht: +f.price_ht || 0,
        estimated_minutes: +f.estimated_minutes || 0,
        corrections_included: +f.corrections_included || 0, formats: f.formats,
        creations: f.creationsText.split("\n").map((l) => {
          const [name, format, dimensions] = l.split("|").map((x) => (x || "").trim());
          return name ? { name, format: format || "", dimensions: dimensions || "" } : null;
        }).filter(Boolean),
      });
      toast.success("Modèle enregistré");
      setF({ name: "", price_ht: "", estimated_minutes: "", corrections_included: 2, formats: "", creationsText: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gtemplates-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Modèles de projets</DialogTitle>
        <div className="space-y-4 mt-2">
          {list.length > 0 && (
            <div className="space-y-1.5" data-testid="gtemplates-list">
              {list.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm border border-neutral-200 rounded-lg px-3 py-2" data-testid={`gtemplate-${t.id}`}>
                  <span className="font-medium">{t.name} <span className="text-neutral-400 font-normal">· {(t.creations || []).length} création(s){t.price_ht ? ` · ${eur(t.price_ht)} HT` : ""}</span></span>
                  <button onClick={async () => { await api.delete(`/graphisme/templates/${t.id}`); load(); onChanged?.(); }}
                    className="p-1 text-neutral-300 hover:text-red-500 transition-colors" data-testid={`gtemplate-del-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-neutral-100 pt-3 space-y-2.5">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">Nouveau modèle</p>
            <Input placeholder="Nom du modèle (ex : Flyer, Identité visuelle…)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="gtemplate-name" />
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" min="0" placeholder="Prix HT" value={f.price_ht} onChange={(e) => setF({ ...f, price_ht: e.target.value })} data-testid="gtemplate-price" />
              <Input type="number" min="0" placeholder="Temps (min)" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} data-testid="gtemplate-temps" />
              <Input type="number" min="0" placeholder="Allers-retours" value={f.corrections_included} onChange={(e) => setF({ ...f, corrections_included: e.target.value })} data-testid="gtemplate-corrections" />
            </div>
            <Input placeholder="Formats (ex : A5, 1080×1080)" value={f.formats} onChange={(e) => setF({ ...f, formats: e.target.value })} data-testid="gtemplate-formats" />
            <Textarea rows={3} placeholder={"Créations prévues, une par ligne :\nCarte A4 | A4 | 210×297 mm\nFlyer | A5 |"} value={f.creationsText}
              onChange={(e) => setF({ ...f, creationsText: e.target.value })} data-testid="gtemplate-creations" />
            <Button onClick={submit} disabled={busy || !f.name.trim()} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="gtemplate-save">
              {busy ? "Enregistrement…" : "Créer le modèle"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CardMenu({ p, onAction }) {
  const [open, setOpen] = useState(false);
  const items = [
    { id: "open", label: "Ouvrir", icon: Search, show: true },
    { id: "quote", label: p.quote_id ? "Voir le devis" : "Créer le devis", icon: FileText, show: !["termine", "archive"].includes(p.status) },
    { id: "planifier", label: "Planifier", icon: CalendarClock, show: ["a_planifier", "planifie_partiel", "planifie", "acompte_a_regler"].includes(p.status) },
    { id: "duplicate", label: "Dupliquer", icon: Copy, show: true },
    { id: "task", label: "Ajouter une tâche", icon: ListTodo, show: !["archive"].includes(p.status) },
    { id: "termine", label: "Marquer terminé", icon: CheckCircle2, show: !["termine", "archive"].includes(p.status) },
    { id: "archive", label: "Archiver", icon: Archive, show: true },
  ].filter((i) => i.show);
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button className="p-1.5 rounded-md text-neutral-300 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
        data-testid={`gcard-menu-${p.id}`} onClick={() => setOpen((v) => !v)}>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-40 bg-white border border-neutral-200 rounded-xl shadow-lg py-1 w-48" data-testid={`gcard-menu-list-${p.id}`}>
            {items.map((i) => (
              <button key={i.id} onClick={() => { setOpen(false); i.id === "task" ? (async () => { try { await api.post("/tasks", { title: `Projet graphisme — ${p.name || p.title}`, client_id: p.client_id, service: "Graphisme", source_type: "graphic_project", source_id: p.id, source_label: `Projet graphique — ${p.name || p.title}`, source_url: `/graphisme/projet/${p.id}` }); toast.success("Tâche ajoutée à la To-do"); window.dispatchEvent(new Event("sf:tasks")); } catch { toast.error("Création impossible"); } })() : onAction(i.id, p); }} data-testid={`gcard-action-${i.id}-${p.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50 transition-colors">
                <i.icon className="w-3.5 h-3.5 text-neutral-400" /> {i.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const fmtMin = (m = 0) => `${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, "0")}`;

function RentabilityModal({ open, onClose, onSelect }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (open) api.get("/graphisme/rentability").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [open]);
  const totals = useMemo(() => ({
    spent: (rows || []).reduce((n, r) => n + r.spent_minutes, 0),
    price: (rows || []).reduce((n, r) => n + r.price_ht, 0),
  }), [rows]);
  const globalRate = totals.spent > 0 && totals.price > 0 ? Math.round(totals.price / (totals.spent / 60)) : null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="grentability" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">Rentabilité par projet — temps de production</DialogTitle>
        <p className="text-xs text-neutral-400 -mt-1">Temps chrono réel vs temps estimé vs prix facturé. Cliquez sur un projet pour l'ouvrir dans le workspace.</p>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1.5 mt-2">
          {rows === null && <p className="text-xs text-neutral-400 py-6 text-center">Chargement…</p>}
          {rows?.length === 0 && <p className="text-xs text-neutral-400 py-6 text-center">Aucun projet actif.</p>}
          {(rows || []).map((r) => {
            const st = gstatus(r.status);
            const pct = r.estimated_minutes > 0 ? Math.min(100, Math.round((r.spent_minutes / r.estimated_minutes) * 100)) : (r.spent_minutes > 0 ? 100 : 0);
            const over = r.estimated_minutes > 0 && r.spent_minutes > r.estimated_minutes;
            return (
              <button key={r.id} onClick={() => onSelect(r.id)} data-testid={`grent-${r.id}`}
                className="w-full flex items-center gap-3 border border-neutral-100 rounded-xl px-3 py-2.5 hover:border-[#93C5FD] hover:bg-[#EFF6FF]/50 transition-colors text-left">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#0F172A] truncate">{r.name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{r.client_name} · {r.number}</p>
                </div>
                <div className="w-32 shrink-0">
                  <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${over ? "bg-red-400" : "bg-[#3B82F6]"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className={`text-[9px] mt-0.5 ${over ? "text-red-500 font-semibold" : "text-neutral-400"}`}>
                    {fmtMin(r.spent_minutes)} / {r.estimated_minutes > 0 ? fmtMin(r.estimated_minutes) : "non estimé"}
                  </p>
                </div>
                <span className="text-xs font-semibold text-neutral-700 w-16 text-right shrink-0">{r.price_ht > 0 ? eur(r.price_ht) : "—"}</span>
                <span className={`text-[10px] font-semibold w-16 text-right shrink-0 ${r.effective_hourly_rate ? "text-emerald-600" : "text-neutral-300"}`}>
                  {r.effective_hourly_rate ? `${String(r.effective_hourly_rate).replace(".", ",")} €/h` : "—"}
                </span>
                <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${st.cls}`}>{st.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-4 pt-2 border-t border-neutral-100 text-xs" data-testid="grentability-totals">
          <span className="text-neutral-500">Total temps passé : <b className="text-neutral-800">{fmtMin(totals.spent)}</b></span>
          <span className="text-neutral-500">CA projets actifs : <b className="text-neutral-800">{eur(totals.price)}</b></span>
          <span className="ml-auto text-neutral-500">Taux moyen : <b className="text-emerald-600">{globalRate ? `${globalRate} €/h` : "—"}</b></span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ACTIVE_STATUSES = ["a_planifier", "planifie", "planifie_partiel", "en_cours", "attente_elements_client", "pret_a_envoyer", "en_modification", "modifications_demandees"];
const DONE_STATUSES = ["partiellement_valide", "projet_valide", "a_facturer", "facture", "attente_reglement", "paye", "livre", "termine"];
const PREVIEW_IMG = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const progressOf = (p) => {
  const cs = p.creations || [];
  const total = Math.max(cs.length, p.qty_creations || 1);
  const done = cs.filter((c) => c.status === "validee").length;
  return Math.round((done / total) * 100);
};
const thumbOf = (p) => {
  for (const c of [...(p.creations || [])].reverse())
    for (const v of [...(c.versions || [])].reverse())
      if (!v.archived) {
        const f = (v.files || []).find((x) => PREVIEW_IMG.includes(x.content_type));
        if (f) return f.id;
      }
  return null;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const fmtSize = (b = 0) => (b >= 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} Mo` : b >= 1024 ? `${Math.round(b / 1024)} Ko` : `${b} o`);
const demStatus = (s) => (s === "nouvelle"
  ? { label: "Nouvelle", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
  : ["devis_envoye", "devis_accepte", "acceptee", "en_cours"].includes(s)
    ? { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" }
    : { label: "À traiter", cls: "bg-rose-50 text-rose-600 border-rose-200" });
const rowStatus = (p) => (p.status === "envoye_validation" || (p.creations || []).some((c) => c.status === "envoyee_validation"))
  ? { label: "À valider", cls: "bg-rose-50 text-rose-600 border-rose-200" } : gstatus(p.status);

import { KanbanView, ClientView, CalendarView } from "@/components/graphisme/GraphismeViews";

const fmtH = (m) => `${Math.floor((m || 0) / 60)}h${String((m || 0) % 60).padStart(2, "0")}`;

export default function Graphisme() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [ws, setWs] = useState(null);
  const [clients, setClients] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({ client_id: "", ptype: "", priority: "", search: "" });
  const [archived, setArchived] = useState(false);
  const [tab, setTab] = useState("tous");
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [rentOpen, setRentOpen] = useState(false);
  const [view, setView] = useState("table");
  const [gdash, setGdash] = useState(null);

  const load = () => {
    api.get("/graphisme/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/graphisme/workspace").then((r) => setWs(r.data)).catch(() => {});
    api.get("/graphisme-planning/dashboard").then((r) => setGdash(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line
  useEffect(() => {
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
    api.get("/graphisme/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const pid = params.get("projet");
    if (pid) setSelectedId(pid);
  }, [params]);

  const hasValidation = (p) => p.status === "envoye_validation" || (p.creations || []).some((c) => c.status === "envoyee_validation");
  const counts = useMemo(() => {
    const base = projects.filter((p) => (archived ? p.archived : !p.archived));
    return {
      tous: base.length,
      en_cours: base.filter((p) => ACTIVE_STATUSES.includes(p.status)).length,
      a_valider: base.filter(hasValidation).length,
      finalises: base.filter((p) => DONE_STATUSES.includes(p.status)).length,
    };
  }, [projects, archived]); // eslint-disable-line

  const visible = useMemo(() => {
    let list = projects.filter((p) => (archived ? p.archived : !p.archived));
    if (tab === "en_cours") list = list.filter((p) => ACTIVE_STATUSES.includes(p.status));
    else if (tab === "a_valider") list = list.filter(hasValidation);
    else if (tab === "finalises") list = list.filter((p) => DONE_STATUSES.includes(p.status));
    if (filters.client_id) list = list.filter((p) => p.client_id === filters.client_id);
    if (filters.ptype) list = list.filter((p) => (p.types || []).includes(filters.ptype));
    if (filters.priority) list = list.filter((p) => p.priority === filters.priority);
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      list = list.filter((p) => [p.name, p.client_name, p.number].some((s) => (s || "").toLowerCase().includes(q)));
    }
    return list;
  }, [projects, archived, tab, filters]); // eslint-disable-line

  useEffect(() => {
    if ((!selectedId || !visible.some((p) => p.id === selectedId)) && visible.length) setSelectedId(visible[0].id);
  }, [visible]); // eslint-disable-line

  const selected = projects.find((p) => p.id === selectedId) || null;
  const activeFilters = Object.values(filters).filter(Boolean).length + (archived ? 1 : 0);

  const cardAction = async (action, p) => {
    try {
      if (action === "open") navigate(`/graphisme/projet/${p.id}`);
      else if (action === "planifier") navigate(`/graphisme/projet/${p.id}?planifier=1`);
      else if (action === "quote") {
        if (p.quote_id) navigate(`/comptabilite?tab=devis&quote=${p.quote_id}`);
        else {
          const r = await api.post(`/graphisme/projects/${p.id}/quote`, {});
          toast.success("Devis créé (brouillon)");
          navigate(`/comptabilite?tab=devis&quote=${r.data.id}`);
        }
      } else if (action === "duplicate") {
        const r = await api.post(`/graphisme/projects/${p.id}/duplicate`);
        toast.success("Projet dupliqué");
        navigate(`/graphisme/projet/${r.data.id}`);
      } else if (action === "termine") {
        await api.patch(`/graphisme/projects/${p.id}`, { status: "termine" });
        toast.success("Projet marqué terminé"); load();
      } else if (action === "archive") {
        await api.post(`/graphisme/projects/${p.id}/archive`, { archived: true });
        toast.success("Projet archivé"); load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const kpis = [
    { id: "actifs", label: "Projets graphiques actifs", icon: FolderOpen, bg: "#E8F0FE", fg: "#1D4ED8",
      value: ws ? ws.kpis.projets_actifs : "—",
      sub: ws ? `↑ +${ws.kpis.projets_created_month}` : "", subLbl: "vs. mois dernier", subCls: "text-emerald-600",
      onClick: () => setTab("tous") },
    { id: "demandes", label: "Demandes clients", icon: FileText, bg: "#E8F0FE", fg: "#1D4ED8",
      value: ws ? ws.kpis.demandes : "—",
      sub: ws ? `↑ +${ws.kpis.demandes_week}` : "", subLbl: "vs. cette semaine", subCls: "text-emerald-600",
      onClick: () => navigate("/demandes") },
    { id: "fichiers", label: "Fichiers à valider", icon: CheckCircle2, bg: "#FEF3E2", fg: "#B45309",
      value: ws ? ws.kpis.fichiers_a_valider : "—",
      sub: ws ? `↑ +${ws.kpis.fichiers_week}` : "", subLbl: "vs. cette semaine", subCls: "text-emerald-600",
      onClick: () => setTab("a_valider") },
    { id: "temps", label: "Temps de production", icon: Clock3, bg: "#FDEAE8", fg: "#B91C1C",
      value: ws ? `${Math.floor(ws.kpis.temps_mois_min / 60)}h${String(ws.kpis.temps_mois_min % 60).padStart(2, "0")}` : "—",
      sub: ws && ws.kpis.temps_pct != null ? `${ws.kpis.temps_pct > 0 ? "↑" : "↓"} ${Math.abs(ws.kpis.temps_pct)} %` : "",
      subLbl: "vs. mois dernier", subCls: ws?.kpis?.temps_pct > 0 ? "text-emerald-600" : "text-red-500", onClick: () => setRentOpen(true) },
  ];

  const TABS = [
    ["tous", "Tous", counts.tous],
    ["en_cours", "En cours", counts.en_cours],
    ["a_valider", "À valider", counts.a_valider],
    ["finalises", "Finalisés", counts.finalises],
  ];

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7.5rem)] overflow-hidden" data-testid="graphisme-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">Graphisme <Palette className="w-5 h-5 text-[#B45309]" /></h1>
          <p className="text-neutral-500 text-xs mt-0.5">Conception et suivi de tous vos projets graphiques clients.</p>
        </div>
        <div className="flex items-center gap-2">
          <p className="hidden 2xl:block text-right text-xs italic text-neutral-400 leading-snug mr-2">« Des visuels qui renforcent<br />les marques et captivent les audiences. »</p>
          <Button variant="outline" size="sm" onClick={() => navigate("/graphisme/documents")} className="gap-1.5" data-testid="gdocuments-open">
            <FileText className="w-3.5 h-3.5" /> Documents
          </Button>
          <Button variant="outline" size="sm" onClick={() => setTplOpen(true)} className="gap-1.5" data-testid="gtemplates-open">
            <LayoutTemplate className="w-3.5 h-3.5" /> Modèles
          </Button>
          <Button size="sm" onClick={() => setModalOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5" data-testid="gproject-new-btn">
            <Plus className="w-3.5 h-3.5" /> Nouveau projet
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0" data-testid="graphisme-kpis">
        {kpis.map((k) => (
          <button key={k.id} onClick={k.onClick || undefined} data-testid={`gkpi-${k.id}`}
            className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5 flex items-center gap-3 text-left hover:shadow-md transition-shadow">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: k.bg }}>
              <k.icon className="w-5 h-5" style={{ color: k.fg }} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-neutral-500 flex items-center justify-between gap-1">{k.label} <ChevronRight className="w-3 h-3 text-neutral-300 shrink-0" /></p>
              <p className="font-heading text-2xl font-bold leading-tight" data-testid={`gkpi-${k.id}-value`}>{k.value}</p>
              {k.sub && <p className="text-[10px]"><span className={`font-semibold ${k.subCls}`}>{k.sub}</span> <span className="text-neutral-400">{k.subLbl}</span></p>}
            </div>
          </button>
        ))}
      </div>

      {gdash && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-white border border-[#E2E8F0] rounded-xl px-4 py-2 text-[11px] text-neutral-500 shrink-0" data-testid="graphisme-dash-band">
          <span data-testid="gdash-demandes">Demandes à traiter : <strong className="text-[#0F172A]">{gdash.demandes_nouvelles}</strong></span>
          <span data-testid="gdash-devis">Devis en attente : <strong className="text-[#0F172A]">{gdash.devis_en_attente}</strong></span>
          <span data-testid="gdash-planifier">À planifier : <strong className="text-[#0F172A]">{gdash.a_planifier}</strong></span>
          <span data-testid="gdash-production">En production : <strong className="text-[#0F172A]">{gdash.en_production}</strong></span>
          <span className={gdash.en_retard > 0 ? "text-red-600 font-semibold" : ""} data-testid="gdash-retard">En retard : <strong>{gdash.en_retard}</strong></span>
          <span data-testid="gdash-validation">Validation client : <strong className="text-[#0F172A]">{gdash.attente_validation_client}</strong></span>
          <span data-testid="gdash-termines">Terminés : <strong className="text-[#0F172A]">{gdash.termines}</strong></span>
          <span data-testid="gdash-planifie">Planifié semaine : <strong className="text-[#0F172A]">{fmtH(gdash.temps_planifie_semaine_min)}</strong></span>
          <span data-testid="gdash-reel">Réel semaine : <strong className="text-[#0F172A]">{fmtH(gdash.temps_reel_semaine_min)}</strong></span>
          <span data-testid="gdash-ca">CA projets : <strong className="text-[#0F172A]">{(gdash.ca_projets_ht || 0).toLocaleString("fr-FR")} € HT</strong></span>
          <span data-testid="gdash-renta">Rentabilité moyenne : <strong className="text-[#0F172A]">{gdash.rentabilite_moyenne_pct != null ? `${gdash.rentabilite_moyenne_pct} %` : "—"}</strong></span>
        </div>
      )}

      <div className="flex items-center gap-1.5 shrink-0" data-testid="graphisme-view-switch">
        {[["table", "Tableau"], ["kanban", "Kanban"], ["client", "Par client"], ["calendrier", "Calendrier"]].map(([id, label]) => (
          <button key={id} onClick={() => setView(id)} data-testid={`gview-${id}`}
            className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${view === id ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Centre : liste + fiche projet */}
      {view === "table" && (
      <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,52%)_minmax(0,48%)] gap-3">
        {/* Liste des projets */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-0" data-testid="graphisme-list">
          <div className="flex items-center gap-2 px-4 pt-3.5 pb-2 shrink-0">
            <FolderOpen className="w-4 h-4 text-neutral-400" />
            <h2 className="font-heading font-bold text-[15px]">Projets graphiques en cours</h2>
            <button onClick={() => { setTab("tous"); setFilters({ client_id: "", ptype: "", priority: "", search: "" }); setArchived(false); }}
              className="ml-auto text-xs font-semibold text-[#0369A1] hover:underline" data-testid="glist-all">Voir tous les projets ›</button>
            <div className="relative">
              <button onClick={() => setFiltersOpen((v) => !v)} data-testid="gfilters-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
                <SlidersHorizontal className="w-3.5 h-3.5" /> Filtres
                {activeFilters > 0 && <span className="w-4 h-4 rounded-full bg-[#0B132B] text-white text-[9px] font-bold flex items-center justify-center">{activeFilters}</span>}
              </button>
              {filtersOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} />
                  <div className="absolute right-0 top-9 z-40 w-64 bg-white border border-neutral-200 rounded-xl shadow-lg p-3 space-y-2" data-testid="gfilters-pop">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                      <Input className="pl-8 h-8 text-xs" placeholder="Rechercher (projet, client, n°)…" value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="gfilter-search" />
                    </div>
                    <select className={`${sel} !py-1.5 text-xs`} value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })} data-testid="gfilter-client">
                      <option value="">Tous les clients</option>
                      {clients.filter(isGraphismeClient).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select className={`${sel} !py-1.5 text-xs`} value={filters.ptype} onChange={(e) => setFilters({ ...filters, ptype: e.target.value })} data-testid="gfilter-type">
                      <option value="">Tous types</option>
                      {(meta?.creation_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select className={`${sel} !py-1.5 text-xs`} value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="gfilter-priority">
                      <option value="">Toutes priorités</option>
                      <option value="urgent">Urgent</option><option value="prioritaire">Prioritaire</option><option value="normal">Normal</option>
                    </select>
                    <button onClick={() => setArchived((v) => !v)} data-testid="gfilter-archives"
                      className="w-full text-xs text-neutral-500 hover:text-neutral-900 border border-neutral-200 rounded-lg px-3 py-1.5 transition-colors flex items-center gap-1.5">
                      <Archive className="w-3 h-3" /> {archived ? "← Projets actifs" : "Voir les archives"}
                    </button>
                    {activeFilters > 0 && (
                      <button onClick={() => { setFilters({ client_id: "", ptype: "", priority: "", search: "" }); setArchived(false); }}
                        className="w-full text-[11px] text-[#0369A1] hover:underline" data-testid="gfilters-reset">Réinitialiser les filtres</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Onglets rapides */}
          <div className="flex items-center gap-5 px-4 border-b border-neutral-100 shrink-0" data-testid="gtabs">
            {TABS.map(([id, label, n]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`gtab-${id}`}
                className={`pb-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
                {label} <span className="text-[9px] text-neutral-300 font-bold">{n}</span>
              </button>
            ))}
          </div>
          {/* Lignes projets — scroll interne uniquement */}
          <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1" data-testid="graphisme-projects">
            {visible.map((p) => {
              const st = rowStatus(p);
              const pct = progressOf(p);
              const thumb = thumbOf(p);
              const people = (p.responsible || "").split(/[,\/]/).map((s) => s.trim()).filter(Boolean).slice(0, 3);
              const isSel = p.id === selectedId;
              return (
                <div key={p.id} onClick={() => setSelectedId(p.id)} data-testid={`gprow-${p.id}`}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition-all ${isSel ? "border-[#93C5FD] bg-[#EFF6FF]" : "border-transparent hover:border-neutral-200 hover:bg-neutral-50"}`}>
                  {thumb ? (
                    <img src={`${API}/graphisme/files/${thumb}`} alt="" className="w-11 h-11 rounded-lg object-cover border border-neutral-100 shrink-0" />
                  ) : (
                    <span className="w-11 h-11 rounded-lg bg-[#F3F4F6] border border-neutral-100 flex items-center justify-center text-[10px] font-bold text-neutral-400 shrink-0">{initialsOf(p.name)}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-bold text-[#0F172A] truncate">{p.name}</p>
                    <p className="text-[11px] text-neutral-400 truncate">{p.client_name}</p>
                  </div>
                  <div className="hidden md:flex gap-2 shrink-0 w-28">
                    {(p.types || []).slice(0, 2).map((t) => <span key={t} className="text-[10px] text-neutral-500 truncate">{t}</span>)}
                  </div>
                  <div className="hidden sm:block w-20 shrink-0">
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#3B82F6] rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[9px] text-neutral-500 mt-0.5">{pct} %</p>
                  </div>
                  <div className="hidden lg:flex -space-x-1.5 shrink-0 w-10">
                    {people.map((n) => (
                      <span key={n} title={n} className="w-6 h-6 rounded-full bg-[#DBEAFE] border-2 border-white text-[8px] font-bold text-[#1D4ED8] flex items-center justify-center">{initialsOf(n)}</span>
                    ))}
                  </div>
                  <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${st.cls}`}>{st.label}</span>
                  <CardMenu p={p} onAction={cardAction} />
                </div>
              );
            })}
            {visible.length === 0 && (
              <div className="border border-dashed border-neutral-200 rounded-xl p-8 text-center text-xs text-neutral-400 m-2" data-testid="graphisme-empty">
                Aucun projet {archived ? "archivé" : "pour ces filtres"}.
              </div>
            )}
          </div>
        </div>

        {/* Fiche projet détaillée */}
        <ProjectPanel projectId={selectedId} clients={clients} meta={meta} onChanged={load} onEdit={(p) => setEditProject(p)} />
      </div>
      )}

      {view !== "table" && (
        <div className="flex-1 min-h-0 overflow-y-auto" data-testid="graphisme-alt-view">
          {view === "kanban" && <KanbanView projects={visible} onOpen={(p) => navigate(`/graphisme/projet/${p.id}`)} />}
          {view === "client" && <ClientView projects={visible} onOpen={(p) => navigate(`/graphisme/projet/${p.id}`)} />}
          {view === "calendrier" && <CalendarView projects={visible} onOpen={(p) => navigate(`/graphisme/projet/${p.id}`)} />}
        </div>
      )}

      {/* Bas : demandes récentes + fichiers à valider */}
      {view === "table" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 shrink-0" style={{ height: 178 }}>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden" data-testid="gdemandes">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100 shrink-0">
            <FileText className="w-3.5 h-3.5 text-neutral-400" />
            <h3 className="font-heading font-bold text-sm">Demandes clients récentes</h3>
            <button onClick={() => navigate("/demandes")} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="gdemandes-all">Voir toutes les demandes ›</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {(ws?.demandes_recentes || []).length === 0 && <p className="text-[11px] text-neutral-400 px-4 py-4">Aucune demande en attente.</p>}
            {(ws?.demandes_recentes || []).map((d) => {
              const ds = demStatus(d.status);
              return (
                <button key={d.id} onClick={() => navigate("/demandes")} data-testid={`gdemande-${d.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                  <span className="w-8 h-8 rounded-lg bg-[#E8F0FE] text-[#1D4ED8] text-[9px] font-bold flex items-center justify-center shrink-0">{initialsOf(d.client_name)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-[#0F172A] truncate">{d.title}</span>
                    <span className="block text-[10px] text-neutral-400 truncate">{d.client_name} · {relTime(d.created_at)}</span>
                  </span>
                  <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${ds.cls}`}>{ds.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden" data-testid="gfiles-validation">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100 shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5 text-neutral-400" />
            <h3 className="font-heading font-bold text-sm">Fichiers à valider</h3>
            <button onClick={() => setTab("a_valider")} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="gfiles-all">Voir tous les fichiers ›</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {(ws?.fichiers_a_valider || []).length === 0 && <p className="text-[11px] text-neutral-400 px-4 py-4">Aucun fichier en attente de validation client.</p>}
            {(ws?.fichiers_a_valider || []).map((f) => (
              <button key={`${f.creation_id}`} onClick={() => setSelectedId(f.project_id)} data-testid={`gfileval-${f.creation_id}`}
                className="w-full flex items-center gap-3 px-4 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="w-8 h-8 rounded-lg bg-[#FEF3E2] flex items-center justify-center shrink-0"><FileText className="w-3.5 h-3.5 text-[#B45309]" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-semibold text-[#0F172A] truncate">{f.file_name} <span className="font-normal text-neutral-400">{f.version_label}</span></span>
                  <span className="block text-[10px] text-neutral-400 truncate">{f.client_name} · modifié {relTime(f.at)}</span>
                </span>
                <span className="text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 bg-rose-50 text-rose-600 border-rose-200">À valider</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      )}

      <ProjectForm open={modalOpen || !!editProject} project={editProject}
        onClose={() => { setModalOpen(false); setEditProject(null); }}
        onSaved={() => { load(); }} clients={clients} meta={meta}
        onClientCreated={(c) => setClients((cs) => [...cs, c])} />
      <TemplatesModal open={tplOpen} onClose={() => setTplOpen(false)} onChanged={() => {}} />
      <RentabilityModal open={rentOpen} onClose={() => setRentOpen(false)}
        onSelect={(id) => { setSelectedId(id); setRentOpen(false); }} />
    </div>
  );
}
